import { ChangeDetectionStrategy, Component, OnInit, OnDestroy, OnChanges, SimpleChanges, ChangeDetectorRef, input, output, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GeneralService, BlastJobDto } from 'src/app/services/general.service';
import { Subject, takeUntil } from 'rxjs';
import { Blast, HoleService } from 'src/app/services/hole.service';
import { DocumentStorageService } from 'src/app/services/document-storage.service';
import { HttpService } from 'src/app/services/http.service';
import { DirectoryType } from 'src/app/bo/fichier';
import { HttpErrorResponse } from '@angular/common/http';
import { FonctionsService } from 'src/app/services/fonctions-partagees.service';
import { UtilisateurService } from 'src/app/services/utilisateur.service';

@Component({
  selector: 'app-blast-history',
  imports: [CommonModule, TranslateModule],
  templateUrl: './blast-history.component.html',
  styleUrl: './blast-history.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BlastHistoryComponent implements OnInit, OnDestroy, OnChanges { 
  readonly tirId = input<number>();
  readonly blast = input<any>();
  readonly qualityData = input<any>();
  readonly collapseChanged = output<boolean>();
  
  public isCollapsed = signal(false);
  public blastJobs = signal<BlastJobDto[]>([]);
  public loading = signal(false);
  public blastData = signal<Blast | null>(null);
  
  private readonly destroy$ = new Subject<void>();
  
  constructor(
    private readonly generalService: GeneralService,
    private readonly holeService: HoleService,
    private readonly cdr: ChangeDetectorRef,
    private readonly documentStorageService: DocumentStorageService,
    private readonly http: HttpService,
    private readonly fonctionsService: FonctionsService,
    private readonly utilService: UtilisateurService,
    private readonly translate: TranslateService
  ) {}

  ngOnInit(): void {
    if (this.tirId()) {
      this.loadBlastJobHistory();
      this.loadBlastData();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tirId'] && changes['tirId'].currentValue && !changes['tirId'].isFirstChange()) {
      this.loadBlastJobHistory();
      this.loadBlastData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  
  public toggleCollapse(): void {
    this.isCollapsed.set(!this.isCollapsed());
    this.collapseChanged.emit(this.isCollapsed());
  }

  private loadBlastJobHistory(): void {
    this.loading.set(true);
    this.generalService.getBlastJobHistory(this.tirId())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (jobs) => {
          this.blastJobs.set(jobs);
          this.loading.set(false);
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error loading blast job history:', error);
          this.loading.set(false);
          this.cdr.detectChanges();
        }
      });
  }

  public getStatusClass(importStatus?: any): string {
    return this.generalService.getStatusClass(importStatus);
  }

  public getSourceTypeDisplay(importStatus?: any): string {
    return this.generalService.getSourceTypeDisplay(importStatus);
  }

  public formatDate(date: Date | string): string {
    if (!date) return '';
    // Ensure UTC dates from backend are interpreted correctly
    let dateStr = typeof date === 'string' ? date : date.toISOString();
    if (!dateStr.endsWith('Z') && !dateStr.includes('+')) {
      dateStr += 'Z';
    }
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';

    return this.generalService.formatDate(d);
  }

  public formatUserName(job: BlastJobDto): string {
    return this.generalService.formatUserName(job);
  }

  public getErrorMessages(importStatus?: any): string {
    if (!importStatus || !importStatus.errors || importStatus.errors.length === 0) {
      return '';
    }
    const fullMessage = importStatus.errors.join('\n');
    return fullMessage.length > 100 ? fullMessage.substring(0, 100) + '...' : fullMessage;
  }

  public getFilePathTooltip(importStatus?: any): string {
    if (!importStatus || !importStatus.filePath) {
      return '';
    }
    // Vérifier si c'est un fichier STR
    const sourceType = String(importStatus.sourceType).toLowerCase();
    if (sourceType === '0' || sourceType === 'strfile') {
      return importStatus.filePath;
    }
    return '';
  }

  public isStrFileSource(importStatus?: any): boolean {
    if (!importStatus) return false;
    const sourceType = String(importStatus.sourceType).toLowerCase();
    return sourceType === '0' || sourceType === 'strfile';
  }

  private loadBlastData(): void {
    if (this.tirId()) {
      this.holeService.GetBlastHoles(this.tirId())
        .then((blast: Blast) => {
          this.blastData.set(blast);
          this.cdr.detectChanges();
        })
        .catch((error) => {
          console.error('Error loading blast data:', error);
        });
    }
  }

  /**
   * Télécharge le fichier STR depuis S3 lorsqu'on clique sur le statut
   */
  public downloadFile(job: BlastJobDto): void {
    if (!job.importStatus || !job.importStatus.filePath) {
      console.warn('No file path available for download');
      return;
    }

    this.fonctionsService.loading_screen(true);
    
    const filePath = job.importStatus.filePath;
    const siteId = this.utilService.currentTir?.siteId || 0;
    const s3key = job.importStatus.filePath; 

    
    this.documentStorageService.getDownloadUrl(s3key, job.jobId, siteId, DirectoryType.USERFILES).subscribe({
        next: (result) => {
            if (result && result.downloadUrl) {
              window.open(result.downloadUrl, '_blank');
              this.fonctionsService.loading_screen(false);
            }
        },
        error: (error: HttpErrorResponse) => {
         this.handleDownloadError(error.message);
        },
      });
  }


  private handleDownloadError(errorMessage: string): void {
    this.fonctionsService.loading_screen(false);
    this.fonctionsService.launch_toast(false, false, errorMessage === 'File not found' ? 'File not found' : 'Error downloading file');
  }

  /**
   * Vérifie si le fichier peut être téléchargé (STR file avec filePath)
   */
  public canDownloadFile(job: BlastJobDto): boolean {
    if (!job.importStatus || !job.importStatus.filePath) {
      return false;
    }
    
    const sourceType = String(job.importStatus.sourceType).toLowerCase();
    return sourceType === '0' || sourceType === 'strfile';
  }

  /**
   * Vérifie si des trous réactivés existent dans ce job
   */
  public hasReenabledHoles(importStatus?: any): boolean {
    return importStatus && importStatus.reEnabledHoles && importStatus.reEnabledHoles.length > 0;
  }

  /**
   * Récupère le message traduit pour les trous réactivés
   */
  public getReenabledHolesTooltip(): string {
    return this.translate.instant('BLAST_HISTORY.REENABLED_HOLES');
  }
}
